import { Col, Container, Row, Badge, Spinner, Image, Card, CardBody } from 'react-bootstrap';
import { useServicesPage } from '@/hooks/useServicesPage';
import { getImageUrl, getMediaUrl, toImageSrc } from '@/utils/imageUrl';
import deFlag from '@/assets/images/flags/de.svg';
import frFlag from '@/assets/images/flags/fr.svg';
import ukFlag from '@/assets/images/flags/uk.svg';
import czFlag from '@/assets/images/flags/cz.svg';
import plFlag from '@/assets/images/flags/pl.svg';

const MultilingualExcursions = ({ sectionData: propSectionData }) => {
  const { section: fetchedSection, loading, error } = useServicesPage('multilingual_excursions');
  const section = propSectionData || fetchedSection;

  const bgColor = section?.background_color || 'light';
  const bgEffect = section?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }

  if (loading && !propSectionData) {
    return (
      <section className={`py-4 py-lg-5 ${bgClass} services-section multilingual-excursions-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }

  if (error || !section || !section.is_active) {
    return null;
  }

  const items = section.items || [];
  const featureItems = items.filter(item => item.item_type === 'feature');
  const cardItems = items.filter(item => item.item_type === 'card');
  const liveLikeLocalItem = items.find(item => item.title?.includes('Live Like Local') || item.title?.includes('live like local'));
  const languagesRaw = section.content?.languages || [];
  const description = section.content?.description || '';
  const languagesTitle = section.content?.languages_title || '';
  const languagesDescription = section.content?.languages_description || '';
  const liveLikeLocalTitle = section.content?.live_like_local_title || '';
  const liveLikeLocalDescription = section.content?.live_like_local_description || '';
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  
  // Get legacy images from content
  const images = section.content?.images || [];
  const languagesImage = images[0] || section.content?.languages_image || null;
  const liveLikeLocalImage = images[1] || section.content?.live_like_local_image || null;

  const resolveImage = (imagePath) => {
    if (!imagePath) return null;
    return getMediaUrl(imagePath) || getImageUrl(imagePath, 'services');
  };

  // Map language strings to flag icons and names
  const languageMap = {
    'German': { flag: deFlag, name: 'German', code: 'DE' },
    'DE': { flag: deFlag, name: 'German', code: 'DE' },
    'de': { flag: deFlag, name: 'German', code: 'DE' },
    'French': { flag: frFlag, name: 'French', code: 'FR' },
    'FR': { flag: frFlag, name: 'French', code: 'FR' },
    'fr': { flag: frFlag, name: 'French', code: 'FR' },
    'English': { flag: ukFlag, name: 'English', code: 'EN' },
    'EN': { flag: ukFlag, name: 'English', code: 'EN' },
    'en': { flag: ukFlag, name: 'English', code: 'EN' },
    'Czech': { flag: czFlag, name: 'Czech', code: 'CZ' },
    'CZ': { flag: czFlag, name: 'Czech', code: 'CZ' },
    'cz': { flag: czFlag, name: 'Czech', code: 'CZ' },
    'Polish': { flag: plFlag, name: 'Polish', code: 'PL' },
    'PL': { flag: plFlag, name: 'Polish', code: 'PL' },
    'pl': { flag: plFlag, name: 'Polish', code: 'PL' },
  };

  // Map languages array to include flag icons
  const languages = languagesRaw.map(lang => {
    const langKey = String(lang).trim();
    return languageMap[langKey] || { flag: ukFlag, name: langKey, code: langKey.toUpperCase() };
  });

  const parseItemContent = (item) => {
    if (!item?.content) return {};
    if (typeof item.content === 'string') {
      try {
        return JSON.parse(item.content);
      } catch {
        return {};
      }
    }
    return item.content || {};
  };

  const normalizeBullets = (content) => {
    if (Array.isArray(content?.bullets)) return content.bullets.filter(Boolean);
    return [];
  };

  const dualBoxItems = cardItems.filter((item) => parseItemContent(item)?.layout === 'dual_box');
  const legacyBoxes = [
    {
      id: 'legacy-languages',
      title: languagesTitle || `Available in ${languages.length} Languages`,
      description: languagesDescription || description,
      background_image: languagesImage,
      icon: null,
      icon_color: null,
      IconComponent: null,
      content: {
        layout: 'dual_box',
        badge: '',
        subtitle: '',
        bullets: languagesRaw,
        image_style: languagesImage ? 'background' : 'header',
        image: languagesImage
      }
    },
    liveLikeLocalItem || liveLikeLocalTitle || liveLikeLocalDescription
      ? {
          id: liveLikeLocalItem?.id || 'legacy-live-like-local',
          title: liveLikeLocalTitle || liveLikeLocalItem?.title || 'Live Like Local',
          description: liveLikeLocalDescription || liveLikeLocalItem?.description || '',
          background_image: liveLikeLocalItem?.background_image || liveLikeLocalImage,
          icon: liveLikeLocalItem?.icon || null,
          icon_color: liveLikeLocalItem?.icon_color || null,
          IconComponent: liveLikeLocalItem?.IconComponent || null,
          content: {
            ...parseItemContent(liveLikeLocalItem),
            layout: 'dual_box',
            badge: parseItemContent(liveLikeLocalItem)?.badge || 'NEW',
            subtitle: parseItemContent(liveLikeLocalItem)?.subtitle || '',
            bullets: parseItemContent(liveLikeLocalItem)?.bullets || [],
            image_style: (liveLikeLocalItem?.background_image || liveLikeLocalImage) ? 'background' : 'header',
            image: liveLikeLocalItem?.background_image || liveLikeLocalImage
          }
        }
      : null
  ].filter(Boolean);
  const boxItems = dualBoxItems.length > 0 ? dualBoxItems : legacyBoxes;

  return (
    <section className={`py-4 py-lg-5 ${bgClass} services-section multilingual-excursions-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`} id="multilingual-excursions">
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            {section.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary mb-3">
                {section.subtitle}
              </Badge>
            )}
            {section.title && (
              <h2 className="sec-title mb-3">
                {section.title}
              </h2>
            )}
            {section.intro_text && (
              <p className="lead intro-text mb-0" dangerouslySetInnerHTML={{ __html: section.intro_text }} />
            )}
          </Col>
        </Row>

        {/* Feature Icons */}
        {featureItems.length > 0 && (
          <Row className="g-4 mb-4">
            {featureItems.map((item) => {
              const Icon = item.IconComponent;
              const colorMap = {
                'primary': 'primary',
                'success': 'success',
                'info': 'info',
                'warning': 'warning',
                'danger': 'danger',
                'dark': 'dark'
              };
              const bgColor = colorMap[item.icon_color] || 'primary';
              
              return (
                <Col key={item.id} sm={6} md={4} lg={2}>
                  <div className="text-center h-100 p-3">
                    {Icon && (
                      <div className={`icon-xl bg-${bgColor} bg-opacity-10 text-${bgColor} rounded-circle flex-centered mx-auto mb-3`}>
                        <Icon size={28} />
                      </div>
                    )}
                    {item.title && (
                      <h6 className="fw-bold mb-0" style={{ color: '#000000' }}>
                        {item.title}
                      </h6>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Dual Box Cards */}
        {boxItems.length > 0 && (
          <Row className="g-4 mb-4">
            {boxItems.map((item, idx) => {
              const Icon = item.IconComponent;
              const content = parseItemContent(item);
              const bullets = normalizeBullets(content);
              const imageStyle = (content.image_style || 'background').toLowerCase();
              const imageSrc = resolveImage(item.background_image || content.image);
              const hasBackground = imageStyle === 'background' && imageSrc;
              const hasHeader = imageStyle === 'header' && imageSrc;
              const badgeText = content.badge || '';
              const subtitleText = content.subtitle || '';
              
              // Check if this is the languages box (has language badges)
              const isLanguagesBox = item.id === 'legacy-languages' || item.title?.includes('Languages') || bullets.some(b => languageMap[String(b).trim()]);
              
              return (
                <Col key={item.id || idx} lg={6}>
                  <Card className={`border-0 shadow-sm h-100 dual-box-card ${hasBackground ? 'dual-box-card--background text-white' : ''}`}>
                    {hasBackground && (
                      <>
                        <div className="dual-box-card-bg" style={{ backgroundImage: `url(${imageSrc})` }} />
                        <div className="dual-box-card-overlay" />
                      </>
                    )}
                    {hasHeader && (
                      <div className="dual-box-card-image">
                        <img src={imageSrc} alt={item.title || 'Card image'} />
                      </div>
                    )}
                    <CardBody className={`dual-box-card-content ${hasBackground ? 'position-relative' : ''}`}>
                      {Icon && (
                        <div className={`icon-lg ${hasBackground ? 'dual-box-icon' : `bg-${item.icon_color || 'primary'} bg-opacity-10 text-${item.icon_color || 'primary'}`} rounded-circle flex-centered ${hasBackground ? '' : 'mb-3'}`}>
                          <Icon size={24} />
                        </div>
                      )}
                      <div className={hasBackground ? 'dual-box-content-bottom' : ''}>
                        {(badgeText || item.title) && (
                          <div className="dual-box-card-title-row mb-2">
                            {item.title && (
                              <h4 className={`fw-bold mb-0 ${hasBackground ? 'text-white' : ''}`} style={{ flex: '1 1 auto', minWidth: 0 }}>
                                {item.title}
                              </h4>
                            )}
                            {badgeText && (
                              <Badge
                                className="dual-box-badge"
                                style={{ flexShrink: 0 }}
                              >
                                {badgeText}
                              </Badge>
                            )}
                          </div>
                        )}
                        {subtitleText && (
                          <div className={`text-uppercase small mb-2 ${hasBackground ? 'text-white-75' : 'text-muted'}`}>
                            {subtitleText}
                          </div>
                        )}
                        {item.description && (
                          <p
                            className={`mb-3 ${hasBackground ? 'text-white-75' : ''}`}
                            style={{ color: hasBackground ? undefined : '#495057', lineHeight: '1.6' }}
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        )}
                        {bullets.length > 0 && (
                          <>
                            {isLanguagesBox ? (
                              // Language badges for languages box - wrap in glass if background
                              <div className={hasBackground ? 'dual-box-glass' : ''} style={{ marginTop: 0, marginBottom: 0 }}>
                                <div className="d-flex flex-wrap gap-2 align-items-center" style={{ margin: 0, padding: 0 }}>
                                  {bullets.map((bullet, bulletIdx) => {
                                    const bulletKey = String(bullet).trim();
                                    const lang = languageMap[bulletKey] || null;
                                    return (
                                      <Badge
                                        key={`${bullet}-${bulletIdx}`}
                                        bg="transparent"
                                        className="language-badge-outline px-3 py-2 d-inline-flex align-items-center gap-2"
                                        style={{
                                          border: hasBackground
                                            ? '1px solid rgba(255, 255, 255, 0.4)'
                                            : '2px solid rgba(var(--bs-primary-rgb), 0.3)',
                                          color: hasBackground ? '#ffffff' : '#000000',
                                          fontSize: '1.125rem',
                                          fontWeight: '600',
                                          margin: 0,
                                          flexShrink: 0
                                        }}
                                      >
                                        {lang?.flag && (
                                          <Image
                                            src={toImageSrc(lang.flag)}
                                            alt={lang.name}
                                            width={20}
                                            height={20}
                                            style={{ objectFit: 'cover', border: '1px solid rgba(0, 0, 0, 0.15)', borderRadius: '2px', flexShrink: 0 }}
                                          />
                                        )}
                                        <span style={{ whiteSpace: 'nowrap' }}>{lang?.name || bullet}</span>
                                      </Badge>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              // Bullet list for other boxes (like Live Like Local) - wrap in glass if background
                              <div className={hasBackground ? 'dual-box-glass' : ''}>
                                <ul className={`mb-0 ps-3 ${hasBackground ? 'text-white-75' : 'text-muted'}`} style={{ marginTop: 0, marginBottom: 0 }}>
                                  {bullets.map((bullet, bulletIdx) => (
                                    <li key={`${bullet}-${bulletIdx}`} className={bulletIdx === bullets.length - 1 ? 'mb-0' : 'mb-2'}>
                                      {bullet}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
    </section>
  );
};

export default MultilingualExcursions;

